import get from 'lodash-es/get';

import { dispatchThunk } from 'app/store';
import { validateField } from 'app/store/data/formsConfig';
import store from 'app/store';
import { QuotationActions } from 'app/store/data/quotation';
import {
  changeFieldValue,
  changeFieldValueAndUpdate,
  getFormValues,
  getQuestion,
  getQuotation,
  setMessageVisibility,
  setQuestionAvailability,
  setQuestionsVisibility,
  setQuestionVisibility,
} from 'app/services/businessFunctionHelpers';
import { getMessageKeyWithSuffix } from 'app/services/validation.service';
import {
  FieldsEnum,
  FormsEnum,
  ModelPathEnum,
  MessagesEnum,
} from 'app/shared/enums';

function revealInitialConsents() {
  const quotation = getQuotation();

  const consentFields = [
    {
      fieldKey: FieldsEnum.ConsentRegulations,
      modelPath: ModelPathEnum.ConsentsRegulationsConsent,
    },
    {
      fieldKey: FieldsEnum.ConsentCepik,
      modelPath: ModelPathEnum.ConsentsCepikConsent,
    },
    {
      fieldKey: FieldsEnum.ConsentMarketing,
      modelPath: ModelPathEnum.ConsentsMarketingConsent,
    },
  ];

  let visibleConsentsCount = 0;

  consentFields.forEach(({ fieldKey, modelPath }) => {
    const isConsentAccepted = get(quotation, modelPath);
    const currentQuestion = getQuestion(
      fieldKey,
      FormsEnum.CalculationFirstStepForm
    );
    const isCurrentlyVisible = currentQuestion?.visible || false;

    const shouldShowField = isCurrentlyVisible || !isConsentAccepted;

    setQuestionVisibility({
      key: fieldKey,
      visible: shouldShowField,
      formName: FormsEnum.CalculationFirstStepForm,
    });

    if (shouldShowField) {
      visibleConsentsCount++;
    }
  });

  setQuestionVisibility({
    key: FieldsEnum.ButtonConsentsCalculation,
    visible: visibleConsentsCount > 1,
    formName: FormsEnum.CalculationFirstStepForm,
  });
}

const firstStep = {
  carRegistrationNumberChangeFirstStep(initialRun?: boolean) {
    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);
    const registrationNumber = get(
      sourceFormValues,
      FieldsEnum.CarRegistrationNumber
    );

    setMessageVisibility({
      key: MessagesEnum.RegistrationNumberNotKnownSuccess,
      visible: !registrationNumber,
      formName: FormsEnum.CalculationFirstStepForm,
    });
  },

  registrationNumberNotKnownChangeFirstStep(initialRun?: boolean) {
    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);
    const registrationNumberNotKnown = get(
      sourceFormValues,
      FieldsEnum.CarRegistrationNumberNotKnown
    );
    const registrationNumber = get(
      sourceFormValues,
      FieldsEnum.CarRegistrationNumber
    );

    let registrationNumberStatus = registrationNumberNotKnown;

    if (initialRun && !!registrationNumber && registrationNumberNotKnown) {
      registrationNumberStatus = false;

      changeFieldValueAndUpdate(
        FieldsEnum.CarRegistrationNumberNotKnown,
        registrationNumberStatus,
        FormsEnum.CalculationFirstStepForm
      );
    } else if (registrationNumberNotKnown && !!registrationNumber) {
      changeFieldValue(
        FieldsEnum.CarRegistrationNumber,
        null,
        FormsEnum.CalculationFirstStepForm
      );

      store.dispatch(
        QuotationActions.setQuotationQuestion(
          FieldsEnum.CarRegistrationNumber,
          null
        )
      );
    }

    setTimeout(
      () =>
        setQuestionAvailability({
          key: FieldsEnum.CarRegistrationNumber,
          enabled: !registrationNumberStatus,
          formName: FormsEnum.CalculationFirstStepForm,
        }),
      0
    );

    setMessageVisibility({
      key: MessagesEnum.RegistrationNumberNotKnownSuccess,
      visible: !registrationNumber || !!registrationNumberStatus,
      formName: FormsEnum.CalculationFirstStepForm,
    });
  },

  birthDateChangeFirstStep(initialRun?: boolean) {
    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);
    const birthDate = get(sourceFormValues, FieldsEnum.OwnerBirthDate);

    if (!birthDate) {
      setMessageVisibility({
        formName: FormsEnum.CalculationFirstStepForm,
        key: getMessageKeyWithSuffix(FieldsEnum.OwnerBirthDate, 'invalid_date'),
        visible: false,
      });
      setMessageVisibility({
        formName: FormsEnum.CalculationFirstStepForm,
        key: getMessageKeyWithSuffix(
          FieldsEnum.OwnerBirthDate,
          'too_young_owner'
        ),
        visible: false,
      });
    }

    if (birthDate !== undefined) {
      revealInitialConsents();
    }
  },

  fillAllRequiredCalculationConsentsFirstStep(initialRun?: boolean) {
    if (initialRun) return;

    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);
    const value = get(sourceFormValues, FieldsEnum.ButtonConsentsCalculation);

    const consentFields = [
      FieldsEnum.ConsentRegulations,
      FieldsEnum.ConsentCepik,
      FieldsEnum.ConsentMarketing,
    ];

    consentFields.forEach((fieldKey) => {
      const question = getQuestion(
        fieldKey as FieldsEnum,
        FormsEnum.CalculationFirstStepForm
      );
      const isQuestionVisible = question?.visible;

      if (isQuestionVisible) {
        changeFieldValue(
          fieldKey as FieldsEnum,
          value,
          FormsEnum.CalculationFirstStepForm
        );
        dispatchThunk(
          validateField({
            questionKey: fieldKey as FieldsEnum,
            formName: FormsEnum.CalculationFirstStepForm,
            value,
          })
        );
        changeFieldValueAndUpdate(
          fieldKey as FieldsEnum,
          value,
          FormsEnum.CalculationForm
        );
      }
    });

    setQuestionsVisibility(
      [FieldsEnum.InsuranceMarketingEmailConsent],
      !value,
      FormsEnum.CalculationForm
    );

    setTimeout(() => {
      changeFieldValue(
        FieldsEnum.ButtonConsentsCalculation,
        value,
        FormsEnum.CalculationFirstStepForm
      );
    }, 0);
  },

  requiredCalculationConsentChangeFirstStep(initialRun?: boolean) {
    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);

    const consent = get(sourceFormValues, FieldsEnum.ConsentRegulations);

    if (consent !== undefined) {
      changeFieldValue(
        FieldsEnum.ConsentRegulations,
        consent,
        FormsEnum.CalculationForm
      );
    }
  },

  onCepikConsentChangeFirstStep(initialRun?: boolean) {
    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);
    const consent = get(sourceFormValues, FieldsEnum.ConsentCepik);

    if (consent !== undefined) {
      changeFieldValue(
        FieldsEnum.ConsentCepik,
        consent,
        FormsEnum.CalculationForm
      );
    }
  },

  onInsuranceMarketingConsentChangeFirstStep(initialRun?: boolean) {
    const sourceFormValues = getFormValues(FormsEnum.CalculationFirstStepForm);
    const consent = get(sourceFormValues, FieldsEnum.ConsentMarketing);

    if (consent !== undefined) {
      changeFieldValue(
        FieldsEnum.ConsentMarketing,
        consent,
        FormsEnum.CalculationForm
      );

      setQuestionsVisibility(
        [FieldsEnum.InsuranceMarketingEmailConsent],
        !consent,
        FormsEnum.CalculationForm
      );
    }
  },
};

export default firstStep;
